import _ from 'lodash';

import {TeammateModel} from '../../../libs/shared/core/src/models/api/teammateModel';
import {isTeamInbox, teammateCanAccessInbox} from '../../../src/helpers/modelHelpers';
import {tagHasBehaviors} from '../../../src/helpers/models/tagHelpers';
import {ConversationModel} from '../../../src/models/api/conversationModel';
import {TagBehaviorsEnum} from '../../../src/models/api/tagModel';
import {AppState} from '../../../src/store/appState';
import {makeGetSortedTrackers} from '../../../src/store/data/conversations/conversationsSelectors';
import {getInboxes} from '../../../src/store/data/inboxes/inboxesSelectors';
import {getTags} from '../../../src/store/data/tags/tagsSelectors';
import {getFindTeammate, getUser} from '../../../src/store/data/teammates/teammatesSelectors';
import {AppParametricSelector, createSelector} from '../../helpers/reselectHelpers';

export interface ConversationCardContainerBaseProps {
  conversation: ConversationModel;
}

/*
 * Selectors.
 */

function getConversation(_state: AppState, props: ConversationCardContainerBaseProps) {
  return props.conversation;
}

export function makeGetInbox() {
  return createSelector([getConversation, getInboxes, getUser], (conversation, inboxes, user) =>
    _(conversation.inboxIds)
      .map((inboxId) => inboxes[inboxId])
      .compact()
      // Exclude private inboxes that belong to other teammates.
      .filter((i) => isTeamInbox(i) || i.namespace === user.namespace)
      .orderBy(
        [
          // 0. Prefer team inboxes.
          (i) => isTeamInbox(i),

          // 1. Prefer inboxes that you can access.
          (i) => teammateCanAccessInbox(user, i),
        ],
        ['desc', 'desc'],
      )
      .first(),
  );
}

export function makeGetAssignee() {
  return createSelector(
    [getConversation, getFindTeammate],
    (conversation, findTeammate) =>
      (conversation.assigneeId && findTeammate(conversation.assigneeId)) || undefined,
  );
}

export function makeGetParticipants(getAssignee: AppParametricSelector<any, TeammateModel | undefined>) {
  return createSelector(
    [makeGetSortedTrackers(getConversation), getFindTeammate, getAssignee, getUser],
    (trackers, findTeammate, assignee, user) =>
      _(trackers)
        .map((tracker) => findTeammate(tracker.teammateId))
        .compact()
        .filter((teammate) => teammate.id !== assignee?.id && teammate.id !== user.id)
        .value(),
  );
}

export function makeGetTags() {
  return createSelector([getConversation, getTags], (conversation, tags) =>
    _(conversation.tagIds)
      .map((id) => tags[id])
      .compact()
      .filter(
        (t) =>
          Boolean(t.showInCells) && !t.isMainInboxTag && tagHasBehaviors(t, TagBehaviorsEnum.CAN_DISPLAY),
      )
      .value(),
  );
}
